import { actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";

import useChallengeArticleContext from "@/challenges/utils/useChallengeArticleContext";
import useIsAuthorizedForIndependentReadingChallenge from "@/challenges/utils/useIsAuthorizedForIndependentReadingChallenge/useIsAuthorizedForIndependentReadingChallenge";
import ChallengeEarnedTokenContent from "@/view/ArticleActivityPicker/ChallengeEarnedTokenContent";
import ALPHABET from "@/view/constants/alphabet";
import QuizResponseItem from "./QuizResponseItem";
import { dividerCss, responseItemContainerCss } from "./QuizSummary.style";
import SummaryItemCount from "./SummaryItemCount";

interface QuizSummaryProps {
  answers: any[];
  isCustomQuiz: boolean;
  questions: any[];
  quizId: string;
  shouldHideQuizAnswers: boolean;
}

const QuizSummary = ({
  answers,
  isCustomQuiz,
  questions,
  quizId,
  shouldHideQuizAnswers,
}: QuizSummaryProps) => {
  const isAuthorizedChallengesPageUser =
    useIsAuthorizedForIndependentReadingChallenge();
  const router = useRouter();
  const { articleData } = useChallengeArticleContext();

  const { pathname, query } = router;

  // TODO: Change this logic once teacher preview experience is added
  if (!answers?.length) {
    return null;
  }

  const createHref = (question: any, index: number) => {
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: "quiz",
        quizId,
        question: isCustomQuiz ? index + 1 : question?.position,
      },
    };
    return href;
  };

  const handleClick = (question: any, index: number) => {
    router.replace(createHref(question, index), undefined, { shallow: true });
  };

  const correctAnswers = answers.filter((answer) => {
    return answer.is_correct === true;
  });

  const renderQuizResponseItem = (question: any, index: number) => {
    // Return an object representing the relevant answer data for the quiz question
    const answerToQuestion = answers.find((answer) => {
      return answer.quiz_question_id === question.id;
    });

    // Returns the question option object where is_correct is true
    const correctAnswer = question.options.find((option: any) => {
      return option.id === answerToQuestion?.correct_answer;
    });

    // Returns the index of the question option object that matches the selected answer
    const selectedAnswer = question.options.find((option: any) => {
      return option.id === answerToQuestion?.value;
    });

    const buttonEventProperties = {
      objectType: objectTypes.quizQuestion,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: "Article",
        description: `Navigate to question ${question?.position} for review`,
        content_id: `${query["levelId"] || ""}`,
        object_id: quizId,
      },
    };

    const quizResponseItemPosition = isCustomQuiz
      ? index + 1
      : question.position;

    const correctAnswerLetter =
      ALPHABET.toUpperCase()[correctAnswer?.position - 1] || "";

    const selectedAnswerLetter =
      ALPHABET.toUpperCase()[selectedAnswer?.position - 1] || "";

    return (
      <QuizResponseItem
        key={`question-item-${question.id}-${quizResponseItemPosition}`}
        buttonEventProperties={buttonEventProperties}
        correctAnswer={correctAnswerLetter}
        handleClick={() => handleClick(question, index)}
        position={quizResponseItemPosition}
        selectedAnswer={selectedAnswerLetter}
        shouldHideQuizAnswers={shouldHideQuizAnswers}
      />
    );
  };
  return (
    <>
      <SummaryItemCount
        correctAnswerCount={correctAnswers.length}
        count={questions.length}
      />
      {isAuthorizedChallengesPageUser && (
        <ChallengeEarnedTokenContent
          isStudent
          articleData={articleData}
          type="Quiz"
        />
      )}
      <div css={responseItemContainerCss}>
        {questions.map((question, index) => {
          return (
            <div key={`question-item-${question.id}`}>
              {renderQuizResponseItem(question, index)}
              {index !== questions.length - 1 && <div css={dividerCss} />}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default QuizSummary;
